import { Plus, Trash2 } from "lucide-react";
import { Field, TextInput, TextArea, inputCls } from "@/components/setup/SetupField";
import { useLang } from "@/i18n";

const EMPTY_PERSON = { role: "", name: "", email: "" };

const PersonListEditor = ({ people, onChange, rolePlaceholder, addLabel, testPrefix, error }) => {
  const { t } = useLang();
  const updateRow = (idx, patch) => onChange(people.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removeRow = (idx) => onChange(people.filter((_, i) => i !== idx));

  return (
    <div>
      {people.length === 0 && <p className="text-xs text-[#999999] mb-2">{t("No entries yet — add the first one below.")}</p>}
      <div className="space-y-2">
        {people.map((p, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.2fr_36px] gap-2 items-start" data-testid={`${testPrefix}-row-${idx}`}>
            <input
              className={inputCls}
              value={p.role}
              onChange={(e) => updateRow(idx, { role: e.target.value })}
              placeholder={t(rolePlaceholder)}
              data-testid={`${testPrefix}-role-${idx}`}
            />
            <input
              className={inputCls}
              value={p.name}
              onChange={(e) => updateRow(idx, { name: e.target.value })}
              placeholder={t("Name")}
              data-testid={`${testPrefix}-name-${idx}`}
            />
            <input
              className={inputCls}
              type="email"
              value={p.email}
              onChange={(e) => updateRow(idx, { email: e.target.value })}
              placeholder="email@cchellenic.com"
              data-testid={`${testPrefix}-email-${idx}`}
            />
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="h-[42px] flex items-center justify-center text-[#999999] hover:text-[#dd260e] transition-colors"
              aria-label="Remove row"
              data-testid={`${testPrefix}-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {error && <p className="text-[11px] text-[#dd260e] font-semibold mt-2">{t(error)}</p>}
      <button
        type="button"
        onClick={() => onChange([...people, { ...EMPTY_PERSON }])}
        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#dd260e] hover:underline mt-3"
        data-testid={`${testPrefix}-add-btn`}
      >
        <Plus className="w-3.5 h-3.5" /> {t(addLabel)}
      </button>
    </div>
  );
};

export const StepTeam = ({ form, update, errors }) => (
  <div className="space-y-8" data-testid="setup-step-team">
    <Field
      label="Training assignments"
      hint="People in your BU that Master F&B should train — e.g. Sales Capability, RSM. They receive product training before go-live."
      testId="setup-field-training"
    >
      <div className="mt-1">
        <PersonListEditor
          people={form.training_assignments}
          onChange={(list) => update({ training_assignments: list })}
          rolePlaceholder="Role — e.g. Sales Capability"
          addLabel="Add person for training"
          testPrefix="setup-training"
          error={errors.training_assignments}
        />
      </div>
    </Field>

    <Field
      label="Main touchpoints"
      hint="Day-to-day programme contacts on your side — the people Master F&B coordinates with for this market."
      testId="setup-field-touchpoints"
    >
      <div className="mt-1">
        <PersonListEditor
          people={form.main_touchpoints}
          onChange={(list) => update({ main_touchpoints: list })}
          rolePlaceholder="Role — e.g. Market lead"
          addLabel="Add touchpoint"
          testPrefix="setup-touchpoint"
          error={errors.main_touchpoints}
        />
      </div>
    </Field>

    <Field
      label="Banner & visuals plan"
      hint="Optional — who develops campaign banners and how. e.g. 'Banners will be developed by our team based on the dimensions provided by Master F&B.'"
      testId="setup-field-banner-plan"
    >
      <TextArea
        value={form.banner_plan}
        onChange={(e) => update({ banner_plan: e.target.value })}
        placeholder="Optional"
        data-testid="setup-input-banner-plan"
      />
    </Field>
  </div>
);
